// src/index.ts
export * from "@dialectlabs/sdk";

// package.json
var version = "2.1.1";

// src/context/DialectContext/index.tsx
import React2, { useContext } from "react";
import { SWRConfig } from "swr";

// src/context/DialectContext/LocalMessages/index.ts
import { useCallback, useState } from "react";

// src/utils/container.tsx
import React from "react";
import { jsx } from "react/jsx-runtime";
var EMPTY = Symbol();
function createContainer(useHook) {
  const Context = React.createContext(EMPTY);
  function Provider(props) {
    const value = useHook(props.initialState);
    return /* @__PURE__ */ jsx(Context.Provider, { value, children: props.children });
  }
  function useContainer() {
    const value = React.useContext(Context);
    if (value === EMPTY) {
      throw new Error("Component must be wrapped with <Container.Provider>");
    }
    return value;
  }
  return { Provider, useContainer };
}

// src/context/DialectContext/LocalMessages/index.ts
function useLocalMessages() {
  const [localMessages, setLocalMessages] = useState({});
  const putLocalMessage = useCallback(
    (threadAddr, msg) => {
      setLocalMessages((prev) => {
        const threadMessages = prev[threadAddr];
        if (!threadMessages) {
          return {
            ...prev,
            [threadAddr]: [msg]
          };
        }
        return {
          ...prev,
          [threadAddr]: [
            msg,
            ...threadMessages.filter(
              (prevMsg) => prevMsg.deduplicationId !== msg.deduplicationId
            )
          ]
        };
      });
    },
    []
  );
  const deleteLocalMessage = useCallback((threadAddr, id) => {
    setLocalMessages((prev) => {
      const threadMessages = prev[threadAddr];
      if (!threadMessages) {
        return {
          ...prev,
          [threadAddr]: []
        };
      }
      return {
        ...prev,
        [threadAddr]: threadMessages.filter(
          (prevMsg) => prevMsg.deduplicationId !== id
        )
      };
    });
  }, []);
  return { localMessages, putLocalMessage, deleteLocalMessage };
}
var LocalMessages = createContainer(useLocalMessages);

// src/context/DialectContext/Sdk/index.ts
import {
  Dialect
} from "@dialectlabs/sdk";
import { useEffect, useMemo } from "react";

// src/context/DialectContext/Wallet/index.ts
import { useCallback as useCallback3, useState as useState3 } from "react";

// src/hooks/internal/useLocalStorage.ts
import { useCallback as useCallback2, useState as useState2 } from "react";
var isLocalStorageAvailable = typeof globalThis.localStorage !== "undefined";
function useLocalStorage(key, initialValue) {
  const [storedValue, setStoredValue] = useState2(() => {
    if (!isLocalStorageAvailable) {
      return initialValue;
    }
    try {
      const item = globalThis.localStorage.getItem(key);
      if (item !== null) {
        return JSON.parse(item);
      }
      globalThis.localStorage.setItem(key, JSON.stringify(initialValue));
      return initialValue;
    } catch (error) {
      console.error(error);
      return initialValue;
    }
  });
  const setValue = useCallback2(
    (value) => {
      if (!isLocalStorageAvailable) {
        console.warn(
          `Tried setting localStorage key \u201C${key}\u201D even though environment is not a browser`
        );
      }
      try {
        setStoredValue(value);
        if (isLocalStorageAvailable) {
          globalThis.localStorage.setItem(key, JSON.stringify(value));
        }
      } catch (error) {
        console.error(error);
      }
    },
    [key]
  );
  return [storedValue, setValue];
}

// src/context/DialectContext/Wallet/constants.ts
var DIALECT_WALLET_CONFIG_STORAGE_KEY = "dialect-wallet-config";

// src/context/DialectContext/Wallet/index.ts
function useDialectWalletStatesHolder() {
  const [walletConnected, setWalletConnected] = useState3(false);
  const [connectionInitiated, setConnectionInitiated] = useState3(false);
  const [
    localStorageHardwareWalletConfig,
    setLocalStorageHardwareWalletConfig
  ] = useLocalStorage(DIALECT_WALLET_CONFIG_STORAGE_KEY, {
    hardwareWalletEnabled: false
  });
  const [hardwareWalletForced, setHardwareWalletForced] = useState3(
    localStorageHardwareWalletConfig.hardwareWalletEnabled
  );
  const handleSetHardwareWalletForced = useCallback3(
    (valOrFunc) => {
      const newState = typeof valOrFunc === "function" ? valOrFunc(hardwareWalletForced) : valOrFunc;
      setHardwareWalletForced(newState);
      setLocalStorageHardwareWalletConfig({
        hardwareWalletEnabled: newState
      });
    },
    [hardwareWalletForced, setLocalStorageHardwareWalletConfig]
  );
  const [isSigningFreeTransaction, setIsSigningFreeTransaction] = useState3(false);
  const [isSigningMessage, setIsSigningMessage] = useState3(false);
  return {
    walletConnected: {
      get: walletConnected,
      set: setWalletConnected
    },
    connectionInitiatedState: {
      get: connectionInitiated,
      set: setConnectionInitiated
    },
    hardwareWalletForcedState: {
      get: hardwareWalletForced,
      set: handleSetHardwareWalletForced
    },
    isSigningFreeTransactionState: {
      get: isSigningFreeTransaction,
      set: setIsSigningFreeTransaction
    },
    isSigningMessageState: {
      get: isSigningMessage,
      set: setIsSigningMessage
    }
  };
}
var DialectWalletStatesHolder = createContainer(
  useDialectWalletStatesHolder
);

// src/context/DialectContext/Sdk/index.ts
var DEFAULT_CONFIG = {
  dialectCloud: {
    tokenStore: "local-storage",
    tokenLifetimeMinutes: 43200
    // 1 month
  }
};
function useDialectSdk({
  config = DEFAULT_CONFIG,
  blockchainSdkFactory
} = {}) {
  const {
    walletConnected: { get: walletConnected },
    connectionInitiatedState: { set: setConnectionInitiated }
  } = DialectWalletStatesHolder.useContainer();
  const sdk = useMemo(() => {
    if (!blockchainSdkFactory || !walletConnected) {
      return null;
    }
    return Dialect.sdk({ ...DEFAULT_CONFIG, ...config }, blockchainSdkFactory);
  }, [config, blockchainSdkFactory, walletConnected]);
  useEffect(
    function preValidateSdkToken() {
      if (!sdk)
        return;
      if (sdk.info.hasValidAuthentication) {
        setConnectionInitiated(true);
      }
    },
    [sdk, setConnectionInitiated]
  );
  return {
    sdk
  };
}
var DialectSdk = createContainer(useDialectSdk);

// src/context/DialectContext/index.tsx
import { jsx as jsx2 } from "react/jsx-runtime";
var DialectContext = React2.createContext(
  {}
);
var useDialectContext = () => {
  return useContext(DialectContext);
};
var DialectContextProvider = ({ config, blockchainSdkFactory, children, dappAddress }) => {
  return /* @__PURE__ */ jsx2(SWRConfig, { children: /* @__PURE__ */ jsx2(DialectContext.Provider, { value: { dappAddress }, children: /* @__PURE__ */ jsx2(DialectSdk.Provider, { initialState: { config, blockchainSdkFactory }, children: /* @__PURE__ */ jsx2(LocalMessages.Provider, { children }) }) }) });
};

// src/hooks/useDapp.ts
import { useMemo as useMemo2 } from "react";
import useSWR from "swr";

// src/utils/index.ts
var EMPTY_OBJ = {};
var EMPTY_ARR = [];

// src/hooks/internal/swrCache.ts
var CACHE_KEY_THREADS = "THREADS";
var CACHE_KEY_THREAD_FN = (findParams) => {
  const prefix = "THREAD_";
  if ("id" in findParams) {
    return prefix + findParams.id.toString();
  }
  if ("otherMembers" in findParams) {
    return prefix + findParams.otherMembers.filter((it) => it).map((it) => it.toString()).join(":");
  }
  throw new Error("should never happen");
};
var CACHE_KEY_MESSAGES_FN = (id) => `MESSAGES_${id}`;
var CACHE_KEY_THREAD_SUMMARY_FN = (otherMembers) => "THREAD_SUMMARY_" + otherMembers.filter((it) => it).map((it) => it.toString()).join(":");
var DAPPS_CACHE_KEY = "DAPPS";
var DAPP_CACHE_KEY_FN = (walletAddress) => "DAPPS_" + walletAddress;
var DAPP_ADDRESSES_CACHE_KEY_FN = (dappAddress) => "DAPP_ADDRESSES_" + dappAddress;
var WALLET_ADDRESSES_CACHE_KEY_FN = (walletAddress) => "WALLET_ADDRESSES_" + walletAddress;
var WALLET_DAPP_ADDRESSES_CACHE_KEY_FN = (walletAddress, dappAddress) => "WALLET_DAPP_ADDRESSES_" + walletAddress + "_" + dappAddress;
var WALLET_NOTIFICATION_SUBSCRIPTIONS_CACHE_KEY_FN = (walletAddress, dappAddress = "") => `WALLET_NOTIFICATION_SUBSCRIPTIONS_${walletAddress}${dappAddress}`;
var DAPP_NOTIFICATION_SUBSCRIPTIONS_CACHE_KEY_FN = (dappAddress) => "DAPP_NOTIFICATION_SUBSCRIPTIONS_" + dappAddress;

// src/hooks/useDialectSdk.ts
var useDialectSdk2 = (unsafe) => {
  const { sdk } = DialectSdk.useContainer();
  if (unsafe) {
    return sdk;
  }
  if (!sdk) {
    throw new Error("sdk not initialized");
  }
  return sdk;
};
var useDialectSdk_default = useDialectSdk2;

// src/hooks/useDapp.ts
function useDapp({
  refreshInterval,
  verified = true,
  blockchainType
} = EMPTY_OBJ) {
  const { dapps } = useDialectSdk_default();
  const {
    wallet: { address: walletAddress }
  } = useDialectSdk_default();
  const { data: dapp, error } = useSWR(
    DAPP_CACHE_KEY_FN(walletAddress),
    () => dapps.find(),
    { refreshInterval, refreshWhenOffline: true }
  );
  const { data: dappsList = EMPTY_ARR } = useSWR(
    DAPPS_CACHE_KEY,
    () => dapps.findAll({ verified, blockchainType }),
    {
      refreshInterval: 0,
      revalidateIfStale: false,
      revalidateOnFocus: false,
      revalidateOnReconnect: false
    }
  );
  const allDapps = useMemo2(
    () => dappsList.reduce(
      (acc, dapp2) => {
        const address = dapp2.address;
        if (!acc[address]) {
          acc[address] = dapp2;
        }
        return acc;
      },
      {}
    ),
    [dappsList]
  );
  return {
    dapp: dapp || null,
    isFetching: !error && dapp === void 0,
    errorFetching: error,
    dapps: allDapps
  };
}
var useDapp_default = useDapp;

// src/hooks/useDappAddresses.ts
import { useEffect as useEffect2 } from "react";
import useSWR2 from "swr";
function useDappAddresses({
  refreshInterval
} = EMPTY_OBJ) {
  const { dapp } = useDapp_default();
  const dappAddressesApi = dapp?.dappAddresses;
  const {
    data: addresses,
    error = null,
    mutate
  } = useSWR2(
    DAPP_ADDRESSES_CACHE_KEY_FN(dapp?.address),
    dappAddressesApi ? () => dappAddressesApi.findAll() : null,
    {
      refreshInterval,
      refreshWhenOffline: true
    }
  );
  useEffect2(
    function invalidateAddresses() {
      mutate();
    },
    [mutate, dappAddressesApi]
  );
  return {
    addresses: addresses || EMPTY_ARR,
    isFetching: Boolean(dapp) && !error && addresses === void 0,
    errorFetching: error
  };
}
var useDappAddresses_default = useDappAddresses;

// src/hooks/useDappNotificationSubscriptions.ts
import useSWR3 from "swr";
function useDappNotificationSubscriptions({
  refreshInterval
} = EMPTY_OBJ) {
  const { dapp } = useDapp_default();
  const subscriptionsApi = dapp?.notificationSubscriptions;
  const { data: subscriptions, error: errorFetching = null } = useSWR3(
    DAPP_NOTIFICATION_SUBSCRIPTIONS_CACHE_KEY_FN(dapp?.address),
    subscriptionsApi ? () => subscriptionsApi.findAll() : null,
    {
      refreshInterval,
      refreshWhenOffline: true
    }
  );
  return {
    subscriptions: subscriptions || EMPTY_ARR,
    isFetching: Boolean(dapp) && !errorFetching && subscriptions === void 0,
    errorFetching
  };
}
var useDappNotificationSubscriptions_default = useDappNotificationSubscriptions;

// src/context/DialectContext/Gate/index.ts
import { useCallback as useCallback4, useEffect as useEffect3, useState as useState4 } from "react";
var PASSING_GATE = () => true;
function useDialectGate(gate = PASSING_GATE) {
  const [isGateLoading, setIsGateLoading] = useState4(false);
  const [isGatePassed, setIsGatePassed] = useState4(false);
  const gateWrapper = useCallback4(async () => {
    setIsGateLoading(true);
    try {
      const gateRes = await gate();
      setIsGatePassed(gateRes);
      return gateRes;
    } catch {
      setIsGatePassed(false);
    } finally {
      setIsGateLoading(false);
    }
  }, [gate]);
  useEffect3(
    function verifyGate() {
      gateWrapper();
    },
    [gateWrapper]
  );
  return {
    isGatePassed,
    isGateLoading
  };
}
var DialectGate = createContainer(useDialectGate);

// src/hooks/useDialectGate.ts
var useDialectGate2 = () => {
  const gateState = DialectGate.useContainer();
  return gateState;
};
var useDialectGate_default = useDialectGate2;

// src/hooks/useDialectWallet.ts
var useDialectWallet = () => {
  const walletCtx = DialectWalletStatesHolder.useContainer();
  return walletCtx;
};
var useDialectWallet_default = useDialectWallet;

// src/hooks/useNotificationChannel.ts
import { useCallback as useCallback5, useState as useState5 } from "react";
import useSWR4 from "swr";
function useNotificationChannel({
  addressType,
  refreshInterval
}) {
  const { wallet: walletsApi } = useDialectSdk_default();
  const [isCreatingAddress, setCreatingAddress] = useState5(false);
  const [isUpdatingAddress, setUpdatingAddress] = useState5(false);
  const [isDeletingAddress, setDeletingAddress] = useState5(false);
  const [isVerifyingCode, setVerifyingCode] = useState5(false);
  const [isSendingCode, setSendingCode] = useState5(false);
  const {
    data: addresses = EMPTY_ARR,
    error: errorFetchingAddresses = null,
    mutate: mutateAddresses
  } = useSWR4(
    WALLET_ADDRESSES_CACHE_KEY_FN(walletsApi.address),
    () => walletsApi.addresses.findAll(),
    {
      refreshInterval,
      refreshWhenOffline: true
    }
  );
  const isFetchingAddresses = !errorFetchingAddresses && addresses === void 0;
  const address = addresses.find((it) => it.type === addressType);
  const errorFetching = errorFetchingAddresses;
  const createAddress = useCallback5(
    async ({ value }) => {
      if (isCreatingAddress) {
        return null;
      }
      setCreatingAddress(true);
      try {
        await walletsApi.addresses.create({
          type: addressType,
          value
        });
        return (await mutateAddresses())?.find((it) => it.type === addressType) || null;
      } finally {
        setCreatingAddress(false);
      }
    },
    [addressType, isCreatingAddress, mutateAddresses, walletsApi]
  );
  const updateAddress = useCallback5(
    async ({ value }) => {
      if (!address || isUpdatingAddress) {
        return null;
      }
      setUpdatingAddress(true);
      try {
        await walletsApi.addresses.update({
          addressId: address.id,
          value
        });
        return (await mutateAddresses())?.find((it) => it.type === addressType) || null;
      } finally {
        setUpdatingAddress(false);
      }
    },
    [address, addressType, isUpdatingAddress, mutateAddresses, walletsApi]
  );
  const deleteAddress = useCallback5(async () => {
    if (!address || isDeletingAddress) {
      return;
    }
    setDeletingAddress(true);
    try {
      await walletsApi.addresses.delete({ addressId: address.id });
      await mutateAddresses();
    } finally {
      setDeletingAddress(false);
    }
  }, [address, isDeletingAddress, mutateAddresses, walletsApi]);
  const verifyCode = useCallback5(
    async ({ code }) => {
      if (!address || isVerifyingCode) {
        return;
      }
      setVerifyingCode(true);
      try {
        await walletsApi.addresses.verify({
          addressId: address.id,
          code
        });
        await mutateAddresses();
      } finally {
        setVerifyingCode(false);
      }
    },
    [address, isVerifyingCode, mutateAddresses, walletsApi]
  );
  const resendCode = useCallback5(async () => {
    if (!address || isSendingCode) {
      return;
    }
    setSendingCode(true);
    try {
      await walletsApi.addresses.resendVerificationCode({
        addressId: address.id
      });
      await mutateAddresses();
    } finally {
      setSendingCode(false);
    }
  }, [address, isSendingCode, mutateAddresses, walletsApi]);
  return {
    globalAddress: address,
    create: createAddress,
    update: updateAddress,
    delete: deleteAddress,
    verify: verifyCode,
    resend: resendCode,
    isCreatingAddress,
    isUpdatingAddress,
    isDeletingAddress,
    isSendingCode,
    isVerifyingCode,
    isFetching: isFetchingAddresses,
    errorFetching
  };
}
var useNotificationChannel_default = useNotificationChannel;

// src/hooks/useNotificationChannelDappSubscription.ts
import { useCallback as useCallback6, useState as useState6 } from "react";
import useSWR5 from "swr";
var useNotificationChannelDappSubscription = ({
  dappAddress,
  addressType
}) => {
  const { wallet: walletsApi } = useDialectSdk_default();
  const { globalAddress } = useNotificationChannel_default({ addressType });
  const [isToggling, setIsToggling] = useState6(false);
  const {
    data: dappSubscriptions = EMPTY_ARR,
    error: errorFetchingDappAddresses = null,
    mutate: mutateDappAddresses
  } = useSWR5(
    WALLET_DAPP_ADDRESSES_CACHE_KEY_FN(walletsApi.address, dappAddress),
    () => walletsApi.dappAddresses.findAll({
      dappAccountAddress: dappAddress
    })
  );
  const currentSubscription = globalAddress ? dappSubscriptions.find((it) => it.address.id === globalAddress.id) : null;
  const toggleSubscription = useCallback6(
    async ({ enabled, address = globalAddress }) => {
      if (!address || !dappAddress || isToggling) {
        return;
      }
      setIsToggling(true);
      try {
        await mutateDappAddresses(
          async () => {
            if (currentSubscription) {
              const updatedSub = await walletsApi.dappAddresses.update({
                dappAddressId: currentSubscription.id,
                enabled
              });
              return dappSubscriptions.map(
                (it) => it.id === updatedSub.id ? updatedSub : it
              );
            } else {
              const newSub = await walletsApi.dappAddresses.create({
                addressId: address.id,
                dappAccountAddress: dappAddress,
                enabled
              });
              return [...dappSubscriptions, newSub];
            }
          },
          {
            optimisticData: dappSubscriptions.find(
              (it) => it.id === currentSubscription?.id
            ) ? dappSubscriptions.map(
              (it) => it.id === currentSubscription?.id ? { ...it, enabled } : it
            ) : [
              ...dappSubscriptions,
              {
                id: "optimistic-dapp-subscription",
                enabled,
                address,
                dappId: dappAddress
              }
            ],
            rollbackOnError: true
          }
        );
      } finally {
        setIsToggling(false);
      }
    },
    [
      currentSubscription,
      dappAddress,
      dappSubscriptions,
      globalAddress,
      isToggling,
      mutateDappAddresses,
      walletsApi.dappAddresses
    ]
  );
  const isFetchingSubscriptions = !errorFetchingDappAddresses && !dappSubscriptions;
  return {
    enabled: currentSubscription?.enabled || false,
    isToggling,
    isFetchingSubscriptions,
    toggleSubscription
  };
};
var useNotificationChannelDappSubscription_default = useNotificationChannelDappSubscription;

// src/hooks/useNotificationDapp.ts
import useSWR6 from "swr";
function useNotificationDapp({
  refreshInterval
} = EMPTY_OBJ) {
  const { dappAddress } = useDialectContext();
  const { dapps } = useDialectSdk_default();
  const { data: dapp, error } = useSWR6(
    DAPP_CACHE_KEY_FN(dappAddress),
    () => dapps.find({ address: dappAddress }),
    { refreshInterval, refreshWhenOffline: true }
  );
  return {
    dapp: dapp || null,
    isFetching: !error && dapp === void 0,
    errorFetching: error
  };
}
var useNotificationDapp_default = useNotificationDapp;

// src/hooks/useNotificationSubscriptions.ts
import {
  DialectSdkError
} from "@dialectlabs/sdk";
import { useCallback as useCallback7, useState as useState7 } from "react";
import useSWR7 from "swr";
function useNotificationSubscriptions({
  dappAddress,
  refreshInterval
}) {
  const {
    wallet: { address: walletAddress, notificationSubscriptions }
  } = useDialectSdk_default();
  const [isUpdating, setIsUpdating] = useState7(false);
  const [errorUpdating, setErrorUpdating] = useState7(
    null
  );
  const {
    data: subscriptions,
    error: errorFetching = null,
    mutate
  } = useSWR7(
    WALLET_NOTIFICATION_SUBSCRIPTIONS_CACHE_KEY_FN(walletAddress, dappAddress),
    notificationSubscriptions ? () => notificationSubscriptions.findAll({ dappAccountAddress: dappAddress }) : null,
    {
      refreshInterval,
      refreshWhenOffline: true
    }
  );
  const update = useCallback7(
    async (command) => {
      setIsUpdating(true);
      setErrorUpdating(null);
      try {
        await mutate(
          (prev) => {
            if (prev) {
              return prev.map(
                (it) => it.notificationType.id === command.notificationTypeId ? {
                  ...it,
                  subscription: {
                    ...it.subscription,
                    config: command.config
                  }
                } : it
              );
            }
            return prev;
          },
          { revalidate: false }
          // optimistic change first
        );
        await notificationSubscriptions.upsert(command);
        await mutate();
      } catch (e) {
        if (e instanceof DialectSdkError) {
          setErrorUpdating(e);
        }
        throw e;
      } finally {
        setIsUpdating(false);
      }
    },
    [mutate, notificationSubscriptions]
  );
  return {
    subscriptions: subscriptions || EMPTY_ARR,
    update,
    isFetching: subscriptions === void 0 && !errorFetching,
    errorFetching,
    isUpdating,
    errorUpdating
  };
}
var useNotificationSubscriptions_default = useNotificationSubscriptions;

// src/hooks/useNotificationThread.ts
import useSWR8 from "swr";
import useSWRMutation from "swr/mutation";
var useNotificationThread = ({
  refreshInterval
} = {}) => {
  const { threads: threadsApi } = useDialectSdk_default();
  const { dappAddress } = useDialectContext();
  const {
    data: thread,
    isLoading,
    error,
    mutate
  } = useSWR8(
    CACHE_KEY_THREAD_FN({ otherMembers: [dappAddress] }),
    () => threadsApi.find({ otherMembers: [dappAddress] }),
    {
      refreshInterval
    }
  );
  const {
    trigger: createThread,
    isMutating: isCreatingThread,
    error: errorCreatingThread
  } = useSWRMutation(
    CACHE_KEY_THREAD_FN({ otherMembers: [dappAddress] }),
    (_, { arg }) => threadsApi.create(arg)
  );
  const {
    trigger: deleteThread,
    isMutating: isDeletingThread,
    error: errorDeletingThread
  } = useSWRMutation(
    CACHE_KEY_THREAD_FN({ otherMembers: [dappAddress] }),
    async () => {
      await thread?.delete();
    }
  );
  return {
    thread: thread ?? null,
    isThreadLoading: isLoading,
    errorLoadingThread: error,
    refreshThread: mutate,
    // todo: consider splitting into atomic hooks?
    create: createThread,
    isCreatingThread,
    errorCreatingThread,
    delete: deleteThread,
    isDeletingThread,
    errorDeletingThread
  };
};
var useNotificationThread_default = useNotificationThread;

// src/hooks/useNotificationThreadMessages.ts
import { SmartMessageStateDto } from "@dialectlabs/sdk";
import { useEffect as useEffect4, useState as useState8 } from "react";
import useSWR9 from "swr";
import useSWRMutation2 from "swr/mutation";
var DEFAULT_INTERVAL = 1e4;
var FASTER_INTERVAL = 3e3;
var hasRunningAction = (message) => {
  if (!message.metadata?.smartMessage?.content.state) {
    return false;
  }
  return [
    SmartMessageStateDto.ReadyForExecution,
    SmartMessageStateDto.Executing
  ].includes(message.metadata.smartMessage.content.state);
};
var useNotificationThreadMessages = ({
  refreshInterval: initialRefreshInterval = DEFAULT_INTERVAL
} = {
  refreshInterval: DEFAULT_INTERVAL
}) => {
  const [refreshInterval, setRefreshInterval] = useState8(
    initialRefreshInterval
  );
  const { thread, isThreadLoading } = useNotificationThread_default();
  const {
    data: messages,
    isLoading,
    error,
    mutate
  } = useSWR9(
    thread ? CACHE_KEY_MESSAGES_FN(thread.id.toString()) : null,
    () => thread?.messages() ?? [],
    {
      refreshInterval
    }
  );
  const { trigger: markAsRead } = useSWRMutation2(
    thread ? CACHE_KEY_THREAD_SUMMARY_FN(
      thread.otherMembers.map((member) => member.address)
    ) : null,
    async () => {
      if (!thread) {
        return;
      }
      await thread.markAsRead();
    }
  );
  useEffect4(() => {
    if (!messages) {
      return;
    }
    let executingAction = false;
    for (const message of messages) {
      if (hasRunningAction(message)) {
        executingAction = true;
        break;
      }
    }
    setRefreshInterval(
      executingAction ? FASTER_INTERVAL : initialRefreshInterval
    );
  }, [messages, initialRefreshInterval]);
  return {
    messages: messages ?? [],
    isMessagesLoading: isLoading || isThreadLoading,
    errorLoadingMessages: error,
    refreshMessages: mutate,
    markAsRead
  };
};
var useNotificationThreadMessages_default = useNotificationThreadMessages;

// src/hooks/useThread.ts
import { DialectSdkError as DialectSdkError2 } from "@dialectlabs/sdk";
import { useCallback as useCallback8, useState as useState9 } from "react";
import useSWR10, { useSWRConfig } from "swr";

// src/utils/scopes.ts
import { ThreadMemberScope } from "@dialectlabs/sdk";
function isWritable(scopes) {
  return scopes.includes(ThreadMemberScope.WRITE);
}
function isAdminable(scopes) {
  return scopes.includes(ThreadMemberScope.ADMIN);
}

// src/hooks/useThread.ts
var useThread = ({
  findParams,
  refreshInterval
}) => {
  const { mutate: globalMutate } = useSWRConfig();
  const { threads: threadsApi } = useDialectSdk_default();
  const [isDeletingThread, setIsDeletingThread] = useState9(false);
  const [errorDeletingThread, setErrorDeletingThread] = useState9(null);
  const {
    data: thread,
    error: errorFetchingThread,
    mutate: mutateThread
  } = useSWR10(
    CACHE_KEY_THREAD_FN(findParams),
    () => threadsApi.find(findParams),
    {
      refreshInterval,
      refreshWhenOffline: true
    }
  );
  const deleteThread = useCallback8(async () => {
    if (!thread)
      return;
    setIsDeletingThread(true);
    setErrorDeletingThread(null);
    try {
      const result = await thread.delete();
      mutateThread();
      globalMutate(CACHE_KEY_THREADS);
      return result;
    } catch (e) {
      if (e instanceof DialectSdkError2) {
        setErrorDeletingThread(e);
      }
      throw e;
    } finally {
      setIsDeletingThread(false);
    }
  }, [globalMutate, mutateThread, thread]);
  return {
    // sdk
    thread: thread || null,
    delete: deleteThread,
    // react-lib
    isFetchingThread: thread === void 0 && !errorFetchingThread,
    errorFetchingThread,
    isDeletingThread,
    errorDeletingThread,
    isWritable: isWritable(thread?.me.scopes || EMPTY_ARR),
    isAdminable: isAdminable(thread?.me.scopes || EMPTY_ARR)
  };
};
var useThread_default = useThread;

// src/hooks/useThreadMessages.ts
import {
  DialectSdkError as DialectSdkError3
} from "@dialectlabs/sdk";
import { nanoid } from "nanoid";
import { useCallback as useCallback9, useMemo as useMemo3, useState as useState10 } from "react";
import useSWR11, { useSWRConfig as useSWRConfig2 } from "swr";
var useThreadMessages = ({
  id,
  refreshInterval
}) => {
  const { thread } = useThread_default({
    findParams: {
      id
    }
  });
  const threadInternal = thread;
  const { mutate: globalMutate } = useSWRConfig2();
  const [isSendingMessage, setIsSendingMessage] = useState10(false);
  const [errorSendingMessage, setErrorSendingMessage] = useState10(null);
  const { localMessages, putLocalMessage, deleteLocalMessage } = LocalMessages.useContainer();
  const {
    data: remoteMessages,
    error: errorFetchingMessages = null,
    mutate
  } = useSWR11(
    threadInternal ? CACHE_KEY_MESSAGES_FN(threadInternal.id.toString()) : null,
    () => threadInternal.messages(),
    { refreshInterval, refreshWhenOffline: true }
  );
  const messages = useMemo3(() => {
    let messageArray = [];
    if (!thread) {
      return null;
    }
    const localThreadMessages = localMessages[thread.id.toString()];
    const filteredLocalMessages = localThreadMessages?.filter((lm) => {
      const remoteMessage = remoteMessages?.find(
        (rm) => rm.deduplicationId === lm.deduplicationId
      );
      if (remoteMessage) {
        deleteLocalMessage(thread.id.toString(), lm.deduplicationId);
      }
      return !remoteMessage;
    });
    if (!filteredLocalMessages && !remoteMessages) {
      return null;
    }
    if (remoteMessages) {
      messageArray = messageArray.concat(remoteMessages);
    }
    if (filteredLocalMessages) {
      messageArray = messageArray.concat(filteredLocalMessages);
    }
    return messageArray.sort((a, b) => b.timestamp.getTime() - a.timestamp.getTime()).map((m) => ({
      ...m,
      deduplicationId: m.deduplicationId ?? m.timestamp.getTime().toString()
    }));
  }, [remoteMessages, thread, localMessages, deleteLocalMessage]);
  const offChainSendMessage = useCallback9(
    async (t, cmd) => {
      setIsSendingMessage(true);
      setErrorSendingMessage(null);
      const threadAddr = t.id.toString();
      const deduplicationId = cmd.deduplicationId ?? nanoid();
      const optimisticMessage = {
        id: deduplicationId,
        deduplicationId,
        text: cmd.text,
        timestamp: /* @__PURE__ */ new Date(),
        author: t.me,
        isSending: true
      };
      try {
        putLocalMessage(threadAddr, optimisticMessage);
        await t.send({ ...cmd, deduplicationId });
        mutate();
        globalMutate(CACHE_KEY_THREADS);
      } catch (e) {
        if (e instanceof DialectSdkError3) {
          setErrorSendingMessage(e);
          putLocalMessage(threadAddr, {
            ...optimisticMessage,
            isSending: false,
            error: e
          });
        }
        throw e;
      } finally {
        setIsSendingMessage(false);
      }
    },
    [globalMutate, mutate, putLocalMessage]
  );
  const sendMessage = useCallback9(
    async (cmd) => {
      if (!threadInternal)
        return;
      const sendMessageFn = offChainSendMessage;
      await sendMessageFn(threadInternal, cmd);
    },
    [threadInternal, offChainSendMessage]
  );
  const cancelMessage = useCallback9(
    async ({ id: id2 }) => {
      if (!thread)
        return;
      deleteLocalMessage(thread.id.toString(), id2);
    },
    [thread, deleteLocalMessage]
  );
  const markAsRead = useCallback9(async () => {
    if (!threadInternal)
      return;
    await threadInternal.markAsRead();
    globalMutate(
      CACHE_KEY_THREAD_SUMMARY_FN(
        threadInternal.otherMembers.map((it) => it.address)
      )
    );
  }, [globalMutate, threadInternal]);
  return {
    messages: messages || EMPTY_ARR,
    send: sendMessage,
    cancel: cancelMessage,
    markAsRead,
    // Do not use `isValidating` since it will produce visual flickering
    isFetchingMessages: !messages && !errorFetchingMessages,
    errorFetchingMessages,
    isSendingMessage,
    errorSendingMessage
  };
};
var useThreadMessages_default = useThreadMessages;

// src/hooks/useThreads.ts
import { DialectSdkError as DialectSdkError4 } from "@dialectlabs/sdk";
import { useCallback as useCallback10, useEffect as useEffect5, useState as useState11 } from "react";
import useSWR12, { useSWRConfig as useSWRConfig3 } from "swr";
var useThreads = ({
  refreshInterval
} = EMPTY_OBJ) => {
  const { threads: threadsApi } = useDialectSdk_default();
  const { mutate: globalMutate } = useSWRConfig3();
  const [isCreatingThread, setIsCreatingThread] = useState11(false);
  const [errorCreatingThread, setErrorCreatingThread] = useState11(null);
  const {
    data: threads,
    error: errorFetchingThreads,
    mutate
  } = useSWR12(CACHE_KEY_THREADS, () => threadsApi.findAll(), {
    refreshInterval,
    refreshWhenOffline: true
  });
  useEffect5(
    function invalidateThreads() {
      mutate();
    },
    [mutate, threadsApi]
  );
  const createThread = useCallback10(
    async (cmd) => {
      setIsCreatingThread(true);
      setErrorCreatingThread(null);
      try {
        const res = await threadsApi.create(cmd);
        await mutate();
        await globalMutate(CACHE_KEY_THREAD_FN({ id: res.id }), res);
        await globalMutate(
          CACHE_KEY_THREAD_FN({
            otherMembers: cmd.otherMembers.map((it) => it.address)
          }),
          res
        );
        return res;
      } catch (e) {
        if (e instanceof DialectSdkError4) {
          setErrorCreatingThread(e);
        }
        throw e;
      } finally {
        setIsCreatingThread(false);
      }
    },
    [threadsApi, mutate, globalMutate]
  );
  return {
    threads: threads || EMPTY_ARR,
    create: createThread,
    // Do not use `isValidating` since it will produce visual flickering
    isFetchingThreads: threads == void 0 && !errorFetchingThreads,
    errorFetchingThreads,
    isCreatingThread,
    errorCreatingThread
  };
};
var useThreads_default = useThreads;

// src/hooks/useUnreadNotifications.ts
import useSWR13 from "swr";
function useUnreadNotifications({
  refreshInterval,
  revalidateOnMount = true,
  revalidateOnFocus = true
} = EMPTY_OBJ) {
  const sdk = useDialectSdk_default(true);
  const { dappAddress } = useDialectContext();
  const { data, mutate } = useSWR13(
    CACHE_KEY_THREAD_SUMMARY_FN([dappAddress]),
    () => sdk?.threads.findSummary({
      otherMembers: [dappAddress]
    }),
    {
      refreshInterval,
      refreshWhenOffline: true,
      revalidateOnMount,
      revalidateOnFocus
    }
  );
  return {
    hasNotificationsThread: data !== null,
    hasUnreadMessages: Boolean(data?.me?.hasUnreadMessages),
    refresh: mutate,
    unreadCount: data?.me?.unreadMessagesCount ?? 0
  };
}
var useUnreadNotifications_default = useUnreadNotifications;
export {
  DialectContextProvider,
  DialectWalletStatesHolder,
  version as REACT_SDK_VERSION,
  useDapp_default as useDapp,
  useDappAddresses_default as useDappAddresses,
  useDappNotificationSubscriptions_default as useDappNotificationSubscriptions,
  useDialectContext,
  useDialectGate_default as useDialectGate,
  useDialectSdk_default as useDialectSdk,
  useDialectWallet_default as useDialectWallet,
  useNotificationChannel_default as useNotificationChannel,
  useNotificationChannelDappSubscription_default as useNotificationChannelDappSubscription,
  useNotificationDapp_default as useNotificationDapp,
  useNotificationSubscriptions_default as useNotificationSubscriptions,
  useNotificationThread_default as useNotificationThread,
  useNotificationThreadMessages_default as useNotificationThreadMessages,
  useThread_default as useThread,
  useThreadMessages_default as useThreadMessages,
  useThreads_default as useThreads,
  useUnreadNotifications_default as useUnreadNotifications
};
//# sourceMappingURL=index.js.map